import { Body1, Field, Input } from '@fluentui/react-components';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router';

import { ExpandableTextarea } from '../../../components/ExpandableTextarea/index.js';
import { TemplateVarCallouts } from '../../../components/TemplateVarCallouts/index.js';
import { VariablesEditor } from '../../../components/VariablesEditor/index.js';
import { getJudgeFactoryPath } from '../../../features/judge-factory';
import type { Grader } from '../../../hooks/useGraders/useGraders.interface.js';
import { graderTypeLabel } from '../../../services/grader.interface.js';
import type { BareTemplateToken } from '../../../utils/templateRefs.js';
import type { VariableRow } from '../../AgentsPage/AgentForm/AgentForm.types.js';
import { VARIABLE_TYPE_OPTIONS } from '../../AgentsPage/AgentForm/AgentForm.types.js';
import { useStyles } from '../GraderPage.style.js';
import {
  renderGraderOverviewNotice,
  renderGraderOverviewSections,
} from '../graderTypes/graderTypeRegistry.js';
import { variableRowsFromMap } from '../graderVariables.utils.js';

import { GraderReadOnlyOverview } from './GraderReadOnlyOverview.js';

interface OverviewTabProps {
  judge: Grader;
  workspaceName: string;
  isEditing?: boolean;
  disabled?: boolean;
  displayName?: string;
  onDisplayNameChange?: (value: string) => void;
  description?: string;
  onDescriptionChange?: (value: string) => void;
  modelName?: string;
  onModelNameChange?: (value: string) => void;
  systemPrompt?: string;
  onSystemPromptChange?: (value: string) => void;
  temperature?: string;
  onTemperatureChange?: (value: string) => void;
  topP?: string;
  onTopPChange?: (value: string) => void;
  maxTokens?: string;
  onMaxTokensChange?: (value: string) => void;
  userMessage?: string;
  onUserMessageChange?: (value: string) => void;
  variables?: VariableRow[];
  variableErrors?: Record<string, string>;
  onVariableUpdate?: (id: string, patch: Partial<VariableRow>) => void;
  onVariableAdd?: () => void;
  onVariableRemove?: (id: string) => void;
  /** `$var` names referenced in edited prompts but not yet declared. */
  undeclaredVars?: string[];
  /** Bare `{{…}}` tokens found across the edited prompt fields. */
  bareTokens?: BareTemplateToken[];
  /** Human labels keyed by field, for the callout's "in <field>" note. */
  templateFieldLabels?: Record<string, string>;
  onDeclareVariable?: (name: string) => void;
  onConvertBareToken?: (token: BareTemplateToken) => void;
}

export function OverviewTab({
  judge,
  workspaceName,
  isEditing = false,
  disabled = false,
  displayName = judge.displayName ?? '',
  onDisplayNameChange,
  description = judge.description,
  onDescriptionChange,
  modelName = judge.model ?? '',
  onModelNameChange,
  systemPrompt = judge.systemPrompt ?? '',
  onSystemPromptChange,
  temperature = judge.temperature ?? '',
  onTemperatureChange,
  topP = judge.topP ?? '',
  onTopPChange,
  maxTokens = judge.maxTokens ?? '',
  onMaxTokensChange,
  userMessage = judge.userTemplate ?? '',
  onUserMessageChange,
  variables,
  variableErrors = {},
  onVariableUpdate,
  onVariableAdd,
  onVariableRemove,
  undeclaredVars = [],
  bareTokens = [],
  templateFieldLabels = {},
  onDeclareVariable,
  onConvertBareToken,
}: OverviewTabProps) {
  const styles = useStyles();
  const viewOnly = !isEditing;
  const persistedVariables = useMemo(
    () => variableRowsFromMap(judge.variables),
    [judge.variables],
  );
  const displayedVariables = variables ?? persistedVariables;

  if (!isEditing) {
    return (
      <GraderReadOnlyOverview judge={judge} workspaceName={workspaceName} />
    );
  }

  return (
    <div className={styles.overviewTab}>
      {renderGraderOverviewNotice(judge)}
      <section
        className={styles.overviewCard}
        aria-labelledby="grader-identity"
      >
        <div id="grader-identity" className={styles.overviewCardTitle}>
          Identity
        </div>
        <div className={styles.overviewCardFields}>
          <div className={styles.overviewFieldRow}>
            <Field
              className={styles.overviewFieldColumn}
              label="Name"
              hint="Immutable workspace identifier."
            >
              <Input
                className={styles.fullWidthControl}
                value={judge.name}
                disabled
              />
            </Field>
            <Field className={styles.overviewFieldColumn} label="Display name">
              <Input
                className={styles.fullWidthControl}
                value={displayName}
                placeholder="No display name"
                disabled={viewOnly || disabled}
                onChange={(_, data) => onDisplayNameChange?.(data.value)}
              />
            </Field>
          </div>
          <Field label="Type" hint="Grader kind — immutable.">
            <Input
              className={styles.fullWidthControl}
              value={graderTypeLabel(judge.graderType)}
              disabled
            />
          </Field>
          <ExpandableTextarea
            label="Description"
            placeholder="What does this judge evaluate?"
            rows={2}
            value={description}
            disabled={viewOnly || disabled}
            onChange={(value) => onDescriptionChange?.(value)}
          />
        </div>
      </section>

      <section
        className={styles.overviewCard}
        aria-labelledby="grader-variables"
      >
        <div id="grader-variables" className={styles.overviewCardTitle}>
          Variables
          <span className={styles.overviewCardTitleHelp}>
            Parameterise this judge — keys use UPPER_SNAKE_CASE.
          </span>
        </div>
        <div className={styles.overviewCardFields}>
          {!viewOnly && (
            <TemplateVarCallouts
              undeclaredVars={undeclaredVars}
              bareTokens={bareTokens}
              fieldLabels={templateFieldLabels}
              onDeclare={(name) => onDeclareVariable?.(name)}
              onConvert={(token) => onConvertBareToken?.(token)}
              disabled={disabled}
            />
          )}
          <VariablesEditor
            variables={displayedVariables}
            typeOptions={VARIABLE_TYPE_OPTIONS}
            errors={variableErrors}
            disabled={viewOnly || disabled}
            emptyLabel="No variables defined."
            onUpdate={(id, patch) => onVariableUpdate?.(id, patch)}
            onAdd={() => onVariableAdd?.()}
            onRemove={(id) => onVariableRemove?.(id)}
          />
        </div>
      </section>

      {renderGraderOverviewSections(judge, {
        viewOnly,
        disabled,
        modelName,
        onModelNameChange,
        systemPrompt,
        onSystemPromptChange,
        temperature,
        onTemperatureChange,
        topP,
        onTopPChange,
        maxTokens,
        onMaxTokensChange,
        userMessage,
        onUserMessageChange,
      })}

      {judge.sourceGraderGenId && (
        <section
          className={styles.overviewCard}
          aria-labelledby="grader-source"
        >
          <div id="grader-source" className={styles.overviewCardTitle}>
            Source
          </div>
          <div className={styles.overviewCardFields}>
            <Body1 className={styles.overviewCardBody}>
              Created in the Judge Factory from judge{' '}
              <RouterLink
                className={styles.detailLink}
                to={getJudgeFactoryPath(workspaceName, judge.sourceGraderGenId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {judge.sourceGraderGenName ?? judge.sourceGraderGenId}
              </RouterLink>
              .
            </Body1>
          </div>
        </section>
      )}
    </div>
  );
}
